﻿import { claimTypesEnum } from 'User/utils/claims'

import IconRoyalty from '@mui/icons-material/AccountBalance'
import IconShield from '@mui/icons-material/AdminPanelSettings'
import IconBundle from '@mui/icons-material/AutoAwesomeMotion'
import IconEdition from '@mui/icons-material/Book'
import IconBuild from '@mui/icons-material/Build'
import IconChevronLeft from '@mui/icons-material/ChevronLeft'
import IconCloudDownload from '@mui/icons-material/CloudDownload'
import IconDashboard from '@mui/icons-material/Dashboard'
import IconEvent from '@mui/icons-material/Event'
import IconFace from '@mui/icons-material/Face'
import IconSeries from '@mui/icons-material/FolderCopy'
import IconAssets from '@mui/icons-material/Image'
import IconWork from '@mui/icons-material/LibraryBooks'
import IconConcept from '@mui/icons-material/LightbulbOutlined'
import IconListAlt from '@mui/icons-material/ListAlt'
import IconUpload from '@mui/icons-material/Publish'
import IconSettings from '@mui/icons-material/Settings'
import IconSuperVisor from '@mui/icons-material/SupervisorAccount'
import IconWebhook from '@mui/icons-material/Webhook'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'

import { Authentication } from 'Common/auth/types'
import ColorModeToggle from 'Common/components/theme/ColorModeToggle'
import { useMainNavStore } from 'Common/store/MainNavStore'
import { hasClaim, hasClaimWithValue, isCustomerAdmin } from 'Common/utils/user/auth'

import { RouterListItemButton } from '../links/routerLinks'
import { NewDrawerItem } from './NewDrawerListItem'

type Props = {
    auth: Authentication
}

const drawerWidth = 230

export default function MainNavigation(props: Readonly<Props>) {
    const { auth } = props
    const theme = useTheme()
    const { openMainNav, setOpenMainNav } = useMainNavStore()

    const drawerItems: (NewDrawerItem & {
        displayDividerBefore?: boolean
        displayDividerAfter?: boolean
    })[] = [
        { link: '/mywork', primary: 'My work', icon: IconDashboard, displayDividerAfter: true },
        {
            link: '/royalty/agreements',
            primary: 'Royalty',
            icon: IconRoyalty,
            isActive: hasClaimWithValue(auth, 'opus_royalty', 'user'),
        },
        { link: '/concepts', primary: 'Concepts', icon: IconConcept },
        { link: '/works', primary: 'Works', icon: IconWork },
        { link: '/work/editions', primary: 'Editions', icon: IconEdition },
        { link: '/bundle/list', primary: 'Bundles', icon: IconBundle },
        {
            link: '/series/list',
            primary: 'Series',
            icon: IconSeries,
            isActive: hasClaim(auth, claimTypesEnum.enum.opus_work),
        },
        {
            link: '/work/activities',
            primary: 'Activities',
            icon: IconEvent,
        },
        {
            link: '/assets',
            primary: 'Assets',
            icon: IconAssets,
            isActive: hasClaim(auth, claimTypesEnum.enum.opus_asset),
        },
        { link: '/contacts', primary: 'Contacts', icon: IconSuperVisor },
        { link: '/contacts/lists', primary: 'Selection lists', icon: IconListAlt },
        { link: '/mydownloads', primary: 'Downloads', icon: IconCloudDownload },
        {
            link: '/imports',
            primary: 'Imports',
            icon: IconUpload,
            isActive:
                hasClaimWithValue(auth, 'opus_royalty', 'user') ||
                hasClaim(auth, 'opus_integration') ||
                isCustomerAdmin(auth),
        },
        {
            link: '/admin',
            primary: 'Admin',
            icon: IconSettings,
            isActive: isCustomerAdmin(auth),
            displayDividerBefore: true,
        },
        {
            link: '/users',
            primary: 'Users',
            icon: IconFace,
            isActive: hasClaimWithValue(auth, 'opus_user', 'admin'),
        },
        {
            link: '/metadata',
            primary: 'Metadata',
            icon: IconBuild,
            isActive: hasClaimWithValue(auth, 'opus_metadata', 'admin'),
        },
        {
            link: '/integrations/documentation',
            primary: 'Integrations',
            icon: IconWebhook,
            isActive:
                hasClaimWithValue(auth, 'opus_user', 'customeradmin') ||
                hasClaimWithValue(auth, 'opus_user', 'admin'),
        },
    ].filter((item) => item !== null && (item.isActive === undefined || item.isActive))

    const administrationItem: NewDrawerItem = {
        link: '/administration',
        primary: 'Administration',
        icon: IconShield,
    }
    const showAdministration = hasClaimWithValue(auth, 'opus_user', 'admin')

    const renderNavItem = (item: NewDrawerItem) => {
        const IconComponent = item.icon
        return (
            <Tooltip
                key={`desktop-${item.primary}`}
                title={item.primary}
                placement="right"
                disableHoverListener={openMainNav}
            >
                <RouterListItemButton
                    nativeButton={false}
                    to={item.link ?? ''}
                    sx={{
                        color: 'inherit',
                        textDecoration: 'none',
                        '&.active': {
                            backgroundColor: 'action.selected',
                        },
                    }}
                >
                    <ListItemIcon>
                        <IconComponent />
                    </ListItemIcon>
                    <ListItemText primary={item.primary} />
                </RouterListItemButton>
            </Tooltip>
        )
    }

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            open={openMainNav}
            sx={{
                width: openMainNav ? drawerWidth : `calc(${theme.spacing(7)} + 1px)`,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                '& .MuiDrawer-paper': {
                    width: openMainNav ? drawerWidth : `calc(${theme.spacing(7)} + 1px)`,
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: openMainNav
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                    }),
                    overflowX: 'hidden',
                },
                display: { xs: 'flex' },
                displayPrint: 'none',
            }}
        >
            <Box
                sx={{
                    ...theme.mixins.toolbar,
                    display: 'flex',
                    alignItems: 'center',
                    p: 0,
                }}
            >
                <IconButton disableRipple onClick={() => setOpenMainNav(!openMainNav)} size="large">
                    <IconChevronLeft fontSize="large" />
                </IconButton>
            </Box>
            <List key="desktop" disablePadding sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <Divider sx={{ mb: 1 }} />
                {drawerItems.map((item) => (
                    <div key={`desktop-nav-${item.primary}`}>
                        {item.displayDividerBefore && (
                            <Divider
                                key={`desktop-divider-${item.primary}`}
                                sx={{ mb: 1, pb: 1 }}
                            />
                        )}
                        {renderNavItem(item)}
                        {item.displayDividerAfter && (
                            <Divider
                                key={`desktop-divider-${item.primary}`}
                                sx={{ mb: 1, pt: 1 }}
                            />
                        )}
                    </div>
                ))}
                <Divider sx={{ mt: 1 }} />
            </List>
            {showAdministration && (
                <List key="desktop-bottom" disablePadding sx={{ mt: 'auto' }}>
                    <Divider />
                    {renderNavItem(administrationItem)}
                    <ColorModeToggle mini={!openMainNav} />
                </List>
            )}
        </Drawer>
    )
}
